import { ClientEnvironment } from './client-environment';
import { AdConsentAnswer, adConsentUnderRequestGpc, parseAdConsentAnswer } from './ad-consent';
import { base64UrlDecodeObject, base64UrlEncodeObject } from './shared-util';

/**
 * The tracking fields a web checkout carries from the browser back to the
 * server, and the rules for what may be done with each (#4045, #4257).
 *
 * Both web payment rails use this. PayPro sends the blob base64-encoded in its
 * `x-data` checkout field; Paddle sends it in `custom_data.tracking`, which
 * Paddle stores on the subscription and echoes on every later webhook. The
 * client writes these keys and the server reads them back, so the two sides must
 * agree exactly — hence one table here rather than string literals spelled by
 * hand at each end, where a typo would silently mean "no attribution" instead of
 * a build error.
 */

/** One checkout's tracking fields. Values are always strings — everything
 *  downstream (the user document, Mixpanel event params and profile properties)
 *  stores them as strings. */
export type CheckoutTrackingData = { [key: string]: string };

/**
 * Everything this module decides about ONE tracking key.
 *
 * Every field is required, and that is the whole point of writing the policies
 * as a record instead of as parallel key lists. A new tracking key does not
 * compile until each of these questions has been answered for it — whereas a new
 * key added to a set of hand-maintained lists is added to some of them and
 * silently left out of the rest, which is how #4096, #5043, #5587 and #5663 each
 * had to be written. The comments on the derived lists below say what each wrong
 * answer costs; none of them fails loudly.
 */
interface TrackingFieldPolicy {
  /**
   * May this key be RECORDED — written onto the user document by
   * `updateUserTelemetryData` and sent to Mixpanel as an event parameter, a
   * profile property, a `<field>_all` union and a `<field>_first` set-once?
   *
   * `false` means the key rides along on the same blob but is used IN TRANSIT
   * ONLY: read for this one purchase and then discarded.
   */
  persisted: boolean;

  /** Does the client read this key straight out of the page URL's query string?
   *  `false` means it is derived from something else — `document.referrer`, the
   *  browser itself, or a value only the server can see. */
  urlSourced: boolean;

  /** May this key be appended to an outbound link? Most of the blob round-trips
   *  through URLs so a click keeps its attribution; a `false` here says this one
   *  is a server-side-only signal that would bloat OTL emails and external URLs
   *  for nothing, and that nothing ever reads back out of a URL. */
  mayAppearInOutboundLink: boolean;

  /** Does this key's presence mean "this visit really came from somewhere" — a
   *  named marketing channel or an actual ad click (#5408)? */
  acquisitionSignal: boolean;

  /** May a brand-new account record this key at creation time (#5408)? */
  recordedAtAccountCreation: boolean;
}

/**
 * Every key a checkout is allowed to carry, and that key's policy.
 *
 * This table is the single source of truth: `TrackingKey` is its key set, and
 * every exported key list below is derived from it, so a key cannot appear in one
 * list and be forgotten in another. The per-key reasoning sits on the key it
 * describes — the paragraphs below are the reason each row reads the way it does,
 * and several of them were written after a live mistake.
 *
 * Declared with `satisfies` rather than annotated `Record<TrackingKey, …>` on
 * purpose: `TrackingKey` is derived FROM this table, so annotating it with that
 * type would be circular. Annotating it anyway (with `Record<string, …>` as the
 * type rather than as a `satisfies` check) would collapse `TrackingKey` to
 * `string` and throw away the exhaustiveness this table exists for.
 */
const TRACKING_FIELD_POLICIES = {
  // the ordinary campaign tags. Read from the URL, recorded, allowed in links,
  // and each one on its own is proof the visit came from a named channel.
  utm_id: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },
  utm_source: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },
  utm_medium: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },
  utm_campaign: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },
  utm_term: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },
  utm_content: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },

  // a device/session correlation id, NOT a channel — so it is not an acquisition
  // signal. The app writes it into its own address bar with no campaign anywhere
  // near it: see the fatal-error page, which rewrites the URL to
  // `?utm_clid=<deviceId>` so a reopened browser can be stitched to the same
  // device. It is still stored whenever something else trips the write.
  utm_clid: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: false,
    recordedAtAccountCreation: true,
  },

  // used by VWO to track experiments: it names an A/B test variant, not a
  // channel, so it is not an acquisition signal either. The client's own "this
  // URL carries campaign tags" test already ignores it — it is the one `utm_*`
  // key missing from `CACHED_TRACKING_KEYS`.
  utm_vwo: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: false,
    recordedAtAccountCreation: true,
  },

  // the two ad click ids. An actual ad click is the strongest acquisition signal
  // there is.
  gclid: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },
  fbclid: {
    persisted: true,
    urlSourced: true,
    mayAppearInOutboundLink: true,
    acquisitionSignal: true,
    recordedAtAccountCreation: true,
  },

  // when the Facebook click id beside it was first seen. Stored right beside the
  // click id it belongs to, and only ever read with it.
  //
  // It has to be recorded rather than transport-only: the trial-to-paid pixel
  // fires ~14 days later, and if it ever had the click id without its capture
  // time it would fall back to "now" — the exact 14-day error #4045 is about.
  // Unlike the buyer's IP this is not personal data, so there is nothing to weigh
  // against keeping it.
  //
  // Not read from the URL — the client stamps it from the running browser — and
  // never put in a link. Not an acquisition signal for a simpler reason than
  // `utm_clid`'s: it never exists without the `fbclid` it belongs to, and a bare
  // timestamp describes nothing.
  fbclid_ts: {
    persisted: true,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: true,
  },

  // where the visit came from, derived from `document.referrer` rather than read
  // from the query string. Not an acquisition signal: every visit has a referrer
  // (the client defaults it to `'direct'`), so treating it as one would trip the
  // account-creation write on every single signup — see
  // `ACQUISITION_SIGNAL_KEYS` for why that would be so expensive.
  $referrer: {
    persisted: true,
    urlSourced: false,
    mayAppearInOutboundLink: true,
    acquisitionSignal: false,
    recordedAtAccountCreation: true,
  },
  $referrer_domain: {
    persisted: true,
    urlSourced: false,
    mayAppearInOutboundLink: true,
    acquisitionSignal: false,
    recordedAtAccountCreation: true,
  },

  // the browser's user agent, used as a Meta match key for event match quality
  // scoring. Read from the running browser, and never put in a link.
  //
  // The ONLY persisted key an account creation may not record. It is always
  // present and describes the SIGNUP browser: stored on the account it would
  // later be offered to Meta as a match key for a purchase made somewhere else
  // entirely — most obviously an in-app store purchase on a phone, described by
  // the user agent of a desktop browser used months earlier. A wrong match key is
  // worse than none.
  client_user_agent: {
    persisted: true,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },

  // the buyer's IP address, which only the server can see. Used for the Mixpanel
  // `$ip` transport field (so the event geo-locates) and as a Meta match key,
  // both of which are per-event uses. Storing it would put a raw IP permanently
  // on the `_users` document and turn it into a queryable Mixpanel profile
  // property, which is personal data we have no reason to keep. PayPro has always
  // treated its `CUSTOMER_IP` this way; this keeps the two rails identical.
  client_ip: {
    persisted: false,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },

  // which device this ONE purchase happened on (#4096). Transport-only for a
  // reason worth spelling out, because persisting them would look harmless: a
  // persisted key is written to the user document AND to four Mixpanel
  // destinations, including a person-property. A person-property holds only its
  // current value with no history, so a person who bought on the web and later
  // installed the phone app would silently rewrite their own past — which is
  // exactly the trap #4096 exists to get out of. They belong on the EVENT, which
  // is written once and never touched again.
  //
  // The browser sends the finished answers rather than raw flags, because only
  // the browser knows both whether it is inside the app shell and which operating
  // system it is on; the vocabulary and the rules that produce them live in
  // `shared/src/client-environment.ts`.
  //
  // Read from the running browser, never from a link: a value round-tripped
  // through a URL would describe whichever device produced the link, not the one
  // paying.
  client_platform: {
    persisted: false,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },
  client_os: {
    persisted: false,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },

  // the address of the page the buyer checked out on, which Meta calls
  // `event_source_url` and requires beside `action_source: 'website'` before it
  // will use a conversion for attribution or ad optimisation (#5043).
  // Transport-only for the same reason as the buyer IP: it describes ONE
  // checkout, it is read for one event, and a persisted copy would become a
  // Mixpanel person-property that a later purchase would silently rewrite.
  //
  // Read from the running browser for the same reason the device fields are: a
  // value carried in a link would name whichever page produced the link, not the
  // page the purchase happened on.
  client_page_url: {
    persisted: false,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },

  // the buyer's answer to the cookie dialog's Advertising choice (#5587): `yes`,
  // `no`, or absent when they never answered. It decides whether this purchase
  // may be uploaded to Meta and Google Ads. Transport-only because the recorded
  // path is first-write-only, and an answer must be replaceable: the server
  // stores it as the account's `advertising` consent record instead (see
  // `shared/src/ad-consent.ts`), and reports it to Mixpanel from there.
  //
  // Read from this browser's cookie at checkout, never from a link — a value
  // carried in a link would be somebody else's answer.
  ad_consent: {
    persisted: false,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },

  // when this browser FIRST saw the Global Privacy Control signal, as an ISO
  // time, or absent when it has never seen one (#5663). It rides along so the
  // server can tell two very different checkouts apart: a browser that knew about
  // the signal and still concluded the person said yes afterwards, and a browser
  // that never saw a signal at all. Only the first of those may keep a "yes" when
  // the SERVER sees the `Sec-GPC` header on the same request — see
  // `applyRequestGpcToCheckoutBlob`.
  //
  // Transport-only for the same reason as the answer beside it: it describes this
  // browser at this moment, and a persisted copy would become a Mixpanel
  // person-property that another device would silently rewrite. Never put in a
  // link either — a value round-tripped through one would describe whichever
  // browser produced the link.
  gpc_since: {
    persisted: false,
    urlSourced: false,
    mayAppearInOutboundLink: false,
    acquisitionSignal: false,
    recordedAtAccountCreation: false,
  },
} satisfies Record<string, TrackingFieldPolicy>;

/** A key the server is willing to read off a checkout. */
export type TrackingKey = keyof typeof TRACKING_FIELD_POLICIES;

/** The keys whose policy answers `true` to the question asked, in table order. */
function trackingKeysWhere(asks: (policy: TrackingFieldPolicy) => boolean): readonly TrackingKey[] {
  const keys = Object.keys(TRACKING_FIELD_POLICIES) as TrackingKey[];
  return keys.filter(key => asks(TRACKING_FIELD_POLICIES[key]));
}

/** Every key a checkout is allowed to carry. */
export const ALLOWED_TRACKING_KEYS: readonly TrackingKey[] = trackingKeysWhere(() => true);

/** Is this one of the keys we accept? Narrows an arbitrary browser-supplied
 *  string to a known field. */
export function isAllowedTrackingKey(key: string): key is TrackingKey {
  return (ALLOWED_TRACKING_KEYS as readonly string[]).includes(key);
}

/**
 * The fields that may be RECORDED: written onto the user document by
 * `updateUserTelemetryData` and sent to Mixpanel as event parameters, profile
 * properties, `<field>_all` unions and `<field>_first` set-onces.
 *
 * These describe the marketing CHANNEL a user arrived through, which is exactly
 * what acquisition reporting is for.
 */
export const PERSISTED_TRACKING_KEYS: readonly TrackingKey[] =
  trackingKeysWhere(policy => policy.persisted);

/**
 * Fields that ride along on the same blob but are used IN TRANSIT ONLY — read
 * for this one purchase and then discarded. They are deliberately NOT written to
 * the user document or to any Mixpanel property; each key's row in the table
 * above says why it is on this side of the line rather than the other.
 */
export const TRANSPORT_ONLY_TRACKING_KEYS: readonly TrackingKey[] =
  trackingKeysWhere(policy => !policy.persisted);

/**
 * The subset the client reads straight out of the page URL's query string. The
 * remaining persisted keys are derived rather than read: `$referrer` /
 * `$referrer_domain` come from `document.referrer`, and `client_user_agent` from
 * the browser.
 */
export const URL_SOURCED_TRACKING_KEYS: readonly TrackingKey[] =
  trackingKeysWhere(policy => policy.urlSourced);

/**
 * Keys that must never be appended to an outbound link. Everything else in the
 * blob round-trips through URLs so a click keeps its attribution, but these are
 * server-side-only signals: putting them in a link would bloat OTL emails and
 * external URLs for nothing, and nothing ever reads them back out of a URL.
 */
export const NON_URL_TRACKING_KEYS: readonly TrackingKey[] =
  trackingKeysWhere(policy => !policy.mayAppearInOutboundLink);

/**
 * The keys whose presence means "this visit really came from somewhere" — a
 * named marketing channel or an actual ad click (#5408).
 *
 * This list is the whole safety of the account-creation write, so it is
 * deliberately narrower than "every `utm_*` key". `sanitizeTrackingForAccountCreation`
 * stores NOTHING unless one of these survived, because
 * `updateUserTelemetryData` is all-or-nothing: once a user document has any
 * `telemetryData`, the payment webhooks can never again record the channel the
 * buyer actually arrived through. So a key that can show up on an ordinary,
 * non-campaign visit would quietly cost us the web attribution that works today.
 *
 * Which persisted keys are deliberately NOT triggers, and why, is written on
 * their rows in the table above — `utm_clid`, `utm_vwo`, `fbclid_ts` and the two
 * referrer fields. None of them is EXCLUDED from storage: when something else
 * trips the condition the whole persisted bag is written, these included. They
 * just cannot trip it alone.
 */
export const ACQUISITION_SIGNAL_KEYS: readonly TrackingKey[] =
  trackingKeysWhere(policy => policy.acquisitionSignal);

/**
 * The keys an ACCOUNT CREATION may record, which is everything
 * `trackingFieldsToPersist` keeps except the browser's user agent. Why that one
 * is excluded is on its row in the table above.
 */
const ACCOUNT_CREATION_TRACKING_KEYS: readonly TrackingKey[] =
  trackingKeysWhere(policy => policy.recordedAtAccountCreation);

/** The longest a single stored tracking value may be. Comfortably above any real
 *  click id or campaign name, and short enough that a whole bag cannot bloat the
 *  user document. */
export const MAX_STORED_TRACKING_VALUE_LENGTH = 200;

/**
 * The subset of a tracking blob that may be recorded on the user document and in
 * Mixpanel — i.e. everything except the transport-only fields above.
 *
 * This matters beyond tidiness. `updateUserTelemetryData` copies the whole blob
 * it is given, and `applyTelemetryDataFromUserInfo` then writes every key it
 * finds to four Mixpanel destinations. So whatever reaches it becomes permanent.
 */
export function trackingFieldsToPersist(tracking: CheckoutTrackingData): CheckoutTrackingData {
  const persisted: CheckoutTrackingData = {};
  for (const key of PERSISTED_TRACKING_KEYS) {
    const value = tracking[key];
    if (value) persisted[key] = value;
  }
  return persisted;
}

/**
 * What a brand-new account may record about where its owner came from (#5408).
 *
 * The input is whatever the browser sent, so it is treated as hostile: it has
 * been through `base64UrlDecodeObject`, which returns `{}` only when the JSON
 * fails to PARSE. Any well-formed JSON passes through untouched — an array, a
 * number, or a deeply nested 20 KB object — and everything that survives here is
 * written to the user document permanently and fanned out to Mixpanel.
 *
 * The four rules, in order:
 *
 * 1. A non-object input (an array, a string, a number, `null`) yields nothing.
 * 2. Only `string` values are kept. A non-string value is DROPPED, never coerced —
 *    a coerced object would land in Mixpanel as the literal text "[object Object]".
 * 3. Each surviving value is truncated, not dropped: a clipped `utm_campaign` is
 *    still useful for channel reporting, whereas dropping it loses the signal.
 * 4. Nothing is returned at all unless a real acquisition signal survived. See
 *    `ACQUISITION_SIGNAL_KEYS` for why that condition carries so much weight.
 *
 * Returns `undefined` — not an empty object — when there is nothing to store, so
 * a caller cannot accidentally write a truthy-but-empty `telemetryData` and trip
 * the very all-or-nothing behaviour rule 4 exists to avoid.
 */
export function sanitizeTrackingForAccountCreation(blob: unknown): CheckoutTrackingData | undefined {
  // rule 1: reject anything that is not a plain object
  if (typeof blob !== 'object' || blob === null || Array.isArray(blob)) return undefined;

  const bag = blob as { [key: string]: unknown };
  const sanitized: CheckoutTrackingData = {};
  for (const key of ACCOUNT_CREATION_TRACKING_KEYS) {
    const value = bag[key];
    // rule 2: strings only, and an empty string says nothing
    if (typeof value !== 'string' || value === '') continue;
    // rule 3: truncate rather than drop
    sanitized[key] = value.slice(0, MAX_STORED_TRACKING_VALUE_LENGTH);
  }

  // a capture time with no click id to belong to describes nothing, and is
  // actively dangerous if stored: a later checkout carrying a click id but no
  // time of its own would leave this orphan in place, and the pair would then be
  // read as one unit — `fb.1.<this time>.<a different click>`, describing a click
  // that never happened (#4045). The client already drops a bare `fbclid_ts`
  // (`resolveClickIdsFromCache`), but this blob is untrusted, so the rule is
  // enforced again here rather than assumed.
  if (!sanitized['fbclid']) delete sanitized['fbclid_ts'];

  // rule 4: a bag with no real acquisition signal is not worth the cost of
  // writing, because writing it would block the payment webhooks for ever
  const hasAcquisitionSignal = ACQUISITION_SIGNAL_KEYS.some(key => sanitized[key]);
  if (!hasAcquisitionSignal) return undefined;

  return sanitized;
}

/** The per-checkout facts `buildCheckoutTrackingFields` adds to the shared bag. */
export interface CheckoutOnlyTrackingFields {
  /** this device's id, used as `utm_clid` when the URL supplied none. */
  deviceId: string;
  /** the buyer's IP, which only the server can see. Absent on the PayPro rail. */
  clientIp?: string;
  /** the address of the page this checkout is happening on, without its query
   *  string (#5043). Optional so a caller with nothing to say adds no key at all:
   *  the server has a fallback address, and an empty string would defeat it. */
  pageUrl?: string;
  /** which device this checkout is happening on (#4096). */
  clientEnvironment: ClientEnvironment;
  /** this browser's Advertising answer (#5587). Absent when the buyer never
   *  answered, and then no key is added — "no answer" is the absence of one. */
  adConsent?: AdConsentAnswer;
  /** when this browser first saw the Global Privacy Control signal (#5663), or
   *  absent when it never has. Its presence is what tells the server the answer
   *  beside it was decided WITH the signal in view. */
  gpcSince?: string;
}

/**
 * Assemble the tracking blob one checkout sends to the server.
 *
 * Kept here, pure, rather than inline in the browser service, because the thing
 * that can go wrong is silent: if a field stops being added, the server simply
 * reports `unknown` forever and nothing fails. Both web rails send the result —
 * PayPro base64-encodes it into `x-data`, Paddle stamps it into
 * `custom_data.tracking`.
 *
 * It copies the shared bag first and never mutates it: that bag is also the one
 * `TelemetryService` spreads onto every event and onto the people profile, so a
 * checkout-only field written into it would escape far beyond this checkout.
 */
export function buildCheckoutTrackingFields(
  base: CheckoutTrackingData,
  extras: CheckoutOnlyTrackingFields,
): CheckoutTrackingData {
  const data: CheckoutTrackingData = { ...base };
  // if the URL carried no click id, fall back to the device id so the server can
  // still correlate this buyer with the session they arrived in
  if (!data['utm_clid']) {
    data['utm_clid'] = extras.deviceId;
  }
  if (extras.clientIp) {
    data['client_ip'] = extras.clientIp;
  }
  if (extras.pageUrl) {
    data['client_page_url'] = extras.pageUrl;
  }
  if (extras.adConsent) {
    data['ad_consent'] = extras.adConsent;
  }
  if (extras.gpcSince) {
    data['gpc_since'] = extras.gpcSince;
  }
  data['client_platform'] = extras.clientEnvironment.platform;
  data['client_os'] = extras.clientEnvironment.os;
  return data;
}

/**
 * Apply a `Sec-GPC: 1` header the SERVER saw to the checkout blob a browser
 * sent with the same request (#5663), and give back the blob to use.
 *
 * This exists for the PayPro rail, where the browser builds the blob and the
 * server embeds it verbatim into the checkout URL — so unlike the Paddle rail
 * there is no round trip in which to tell the page about a header it could not
 * see for itself. Some browsers and extensions send the header without setting
 * the page-visible property, and their purchase would otherwise be reported.
 *
 * Only the Advertising answer is touched; every other field is carried across
 * unchanged, because this blob is also the attribution record the webhook reads
 * back and rewriting it wholesale would lose the channel the buyer arrived
 * through.
 *
 * A "yes" is kept ONLY when the blob also carries `gpc_since` — proof that the
 * browser knew about a signal and still concluded the person said yes after it.
 * Without that stamp the browser never saw a signal at all, so its "yes" was
 * decided in ignorance of the very thing this function is reacting to, and it
 * becomes a "no". That distinction is the whole point on this rail: a browser
 * setting only the header cannot see its own signal, so trusting its "yes"
 * would report exactly the person the ticket is about.
 *
 * A blob that cannot be read is returned UNTOUCHED rather than replaced. The
 * cost of that is one gap — a signalling browser whose blob is corrupt is not
 * opted out by this path — and the cost of the alternative is destroying a real
 * buyer's attribution on every malformed blob, which is the worse trade.
 * `base64UrlDecodeObject` reports a parse failure as an empty object, so "empty
 * and the input was not" is the test.
 */
export function applyRequestGpcToCheckoutBlob(
  encodedBlob: string | undefined,
  requestSaysGpc: boolean,
): string | undefined {
  if (!requestSaysGpc) return encodedBlob;

  const decoded: unknown = base64UrlDecodeObject(encodedBlob);
  if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded)) return encodedBlob;
  const bag = decoded as { [key: string]: unknown };
  // an unreadable blob decodes to {}; so does an absent one, and there the empty
  // object is the honest answer rather than a failure
  if (encodedBlob && Object.keys(bag).length === 0) return encodedBlob;

  const browserSawASignal = typeof bag['gpc_since'] === 'string' && bag['gpc_since'] !== '';
  const answer = adConsentUnderRequestGpc(parseAdConsentAnswer(bag['ad_consent']), true, browserSawASignal);
  if (bag['ad_consent'] === answer) return encodedBlob;
  return base64UrlEncodeObject({ ...bag, ad_consent: answer });
}
